<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="ib-question">
        <span class="ib-bar ib-bar--xl"></span>
      </div>
      <div class="ib-board">
        <!-- Cluster A -->
        <div class="ib-cluster ib-cluster--a">
          <div
            v-for="(n, i) in clusterA"
            :key="'a-' + n.text"
            class="ib-note"
            :class="['--c-' + n.color]"
            :style="{ '--rot': n.rot + 'deg', '--note-i': i }"
          >
            <span>{{ n.text }}</span>
          </div>
        </div>
        <!-- Cluster B -->
        <div class="ib-cluster ib-cluster--b">
          <div
            v-for="(n, i) in clusterB"
            :key="'b-' + n.text"
            class="ib-note"
            :class="['--c-' + n.color]"
            :style="{ '--rot': n.rot + 'deg', '--note-i': i }"
          >
            <span>{{ n.text }}</span>
          </div>
        </div>
        <!-- Active landing note -->
        <div class="ib-note ib-note--landing --c-accent" :style="{ '--rot': '3deg' }">
          <span>Bots</span>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="ib-phone-q"></span>
      <div class="ib-phone-note">
        <span class="ib-phone-text">AI assistants<span class="ib-caret">|</span></span>
      </div>
      <div class="ib-phone-colors">
        <span class="ib-color --c-pink --picked"></span>
        <span class="ib-color --c-blue"></span>
        <span class="ib-color --c-yellow"></span>
        <span class="ib-color --c-green"></span>
      </div>
      <button type="button" class="ib-phone-add" tabindex="-1">
        <span class="material-symbols-rounded">add</span>
        Post
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewIdeaBoard',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      clusterA: [
        { text: 'Onboarding', color: 'pink', rot: -4 },
        { text: 'Tutorials', color: 'pink', rot: 2 },
        { text: 'Demos', color: 'yellow', rot: -1 },
      ],
      clusterB: [
        { text: 'Faster CI', color: 'blue', rot: 3 },
        { text: 'Linting', color: 'blue', rot: -2 },
        { text: 'Tests', color: 'green', rot: 1 },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.ib-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
  width: 70%;
}
.ib-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.ib-board {
  position: relative;
  flex: 1;
  min-height: 140px;
  background:
    radial-gradient(circle at 28% 35%, color-mix(in srgb, var(--accent) 8%, transparent) 0%, transparent 30%),
    radial-gradient(circle at 72% 65%, rgba(255, 121, 198, 0.10) 0%, transparent 28%);
  border-radius: 6px;
}
.ib-cluster {
  position: absolute;
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  width: 86px;
  padding: 6px;
  border: 1.5px dashed color-mix(in srgb, var(--accent) 30%, transparent);
  border-radius: 8px;
}
.ib-cluster--a { top: 8%; left: 6%; }
.ib-cluster--b { top: 38%; right: 6%; }

.ib-note {
  width: 36px; min-height: 26px;
  border-radius: 3px;
  padding: 3px 4px;
  font-size: 8px; font-weight: 700;
  line-height: 1.1;
  transform: rotate(var(--rot, 0deg));
  box-shadow: 0 1px 2px rgba(15, 15, 20, 0.08);
  display: flex; align-items: center;
}
.ib-note.--c-pink   { background: #FFD6E7; color: #B12569; }
.ib-note.--c-blue   { background: #D6E8FF; color: #2B5AA8; }
.ib-note.--c-yellow { background: #FFF4C2; color: #8B6914; }
.ib-note.--c-green  { background: #D6F3DA; color: #2A6B3A; }
.ib-note.--c-accent {
  background: color-mix(in srgb, var(--accent) 22%, white);
  color: var(--accent);
}

.ib-note--landing {
  position: absolute;
  bottom: 12%;
  left: 42%;
  animation: ib-drop 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}

/* ---- Audience phone ---- */
.ib-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.ib-phone-note {
  background: #FFD6E7;
  border-radius: 6px;
  padding: 14px 10px;
  min-height: 56px;
  transform: rotate(-2deg);
  box-shadow: 0 1px 2px rgba(15, 15, 20, 0.08);
}
.ib-phone-text {
  font-size: 11px; font-weight: 700;
  color: #B12569;
}
.ib-caret { color: #B12569; animation: ib-blink 1s steps(2) infinite; }

.ib-phone-colors {
  display: flex; justify-content: center; gap: 6px;
  padding: 4px 0;
}
.ib-color {
  width: 14px; height: 14px;
  border-radius: 999px;
  border: 1.5px solid white;
  box-shadow: 0 0 0 1px rgba(15, 15, 20, 0.1);
}
.ib-color.--c-pink   { background: #FFB1C9; }
.ib-color.--c-blue   { background: #B8D4FF; }
.ib-color.--c-yellow { background: #FFE680; }
.ib-color.--c-green  { background: #B5E6BC; }
.ib-color.--picked {
  box-shadow: 0 0 0 2px var(--accent);
  transform: scale(1.15);
}

.ib-phone-add {
  appearance: none; border: none;
  background: var(--accent); color: white;
  font-family: inherit; font-weight: 700; font-size: 12px;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  cursor: pointer;
  animation: ib-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.ib-phone-add .material-symbols-rounded { font-size: 14px; }

@keyframes ib-drop {
  0%, 12%  { transform: rotate(var(--rot)) translate(-40px, -30px) scale(0.6); opacity: 0; }
  40%      { transform: rotate(var(--rot)) translate(0, 0) scale(1.1); opacity: 1; }
  55%, 92% { transform: rotate(var(--rot)) translate(0, 0) scale(1); opacity: 1; }
  100%     { transform: rotate(var(--rot)) translate(0, 0) scale(1); opacity: 1; }
}
@keyframes ib-blink { 0% { opacity: 1; } 100% { opacity: 0; } }
@keyframes ib-tap {
  0%, 60%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  72%       { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%      { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .ib-note--landing { animation: none; }
  .ib-caret, .ib-phone-add { animation: none; }
}
</style>
